import { ChangeDetectionStrategy, Component, input } from '@angular/core';

/**
 * A page-level card with a navy/gold branded header.
 *
 * Usage:
 *   <ds-page-card eyebrow="..." title="..." subtitle="...">
 *     ...body...
 *     <div ds-page-card-footer>...</div>
 *   </ds-page-card>
 */
@Component({
  selector: 'ds-page-card',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <header class="page-card__header">
      <ng-content select="[ds-page-card-icon]" />
      <div class="page-card__heading">
        @if (eyebrow()) {
          <p class="page-card__eyebrow">{{ eyebrow() }}</p>
        }
        @if (title()) {
          <h1 class="page-card__title" [id]="titleId()">{{ title() }}</h1>
        }
        @if (subtitle()) {
          <p class="page-card__subtitle">{{ subtitle() }}</p>
        }
      </div>
      <ng-content select="[ds-page-card-actions]" />
    </header>
    <div class="page-card__body">
      <ng-content />
    </div>
    <ng-content select="[ds-page-card-footer]" />
  `,
  styles: `
    :host {
      display: block;
      width: min(100%, var(--page-card-maxw, 760px));
      margin: 0 auto;
      overflow: hidden;
      background: var(--color-surface);
      border: 1px solid rgba(32, 51, 93, 0.12);
      border-radius: 24px;
      box-shadow: 0 24px 70px rgba(17, 35, 70, 0.14);
    }

    .page-card__header {
      display: flex;
      align-items: center;
      gap: var(--space-4);
      padding: var(--space-5) var(--space-6);
      color: #ffffff;
      background: #20335d;
      border-bottom: 4px solid #d1a632;
    }

    .page-card__heading {
      min-width: 0;
      flex: 1;
    }

    .page-card__eyebrow {
      margin: 0 0 var(--space-1);
      color: rgba(255, 255, 255, 0.72);
      font-size: var(--text-xs);
      font-weight: var(--font-weight-semibold);
      letter-spacing: 0.09em;
      text-transform: uppercase;
    }

    .page-card__title {
      margin: 0;
      font-family: var(--font-body);
      font-size: var(--text-xl);
      font-weight: var(--font-weight-bold);
      color: #ffffff;
      line-height: var(--leading-heading);
    }

    .page-card__subtitle {
      margin: var(--space-1) 0 0;
      font-size: var(--text-sm);
      color: rgba(255, 255, 255, 0.82);
    }

    .page-card__body {
      padding: var(--space-6);
    }

    @media (max-width: 700px) {
      :host {
        border-radius: 0;
        border-width: 0;
        box-shadow: none;
      }

      .page-card__header {
        padding: var(--space-4);
      }

      .page-card__body {
        padding: var(--space-4);
      }

      .page-card__title {
        font-size: var(--text-lg);
      }

      .page-card__subtitle {
        font-size: var(--text-xs);
      }
    }

    @media (max-width: 420px) {
      .page-card__header {
        align-items: flex-start;
      }

      .page-card__eyebrow {
        font-size: 0.625rem;
      }
    }
  `,
})
export class PageCardComponent {
  eyebrow = input('');
  title = input('');
  subtitle = input('');
  titleId = input('');
}
